import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';
import cn from 'clsx';
import moment from 'moment';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import Loading from '@components-clean/core/loading/loading';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { Owner } from '@interfaces-clean/observations.interface';
import { StudyMemberDto } from '@interfaces-clean/study-member.interface';
import { SubjectActionGridRow } from '@interfaces-clean/subject-monitoring/subject-actions';
import { SubjectObservationType } from '@interfaces-clean/subject-monitoring/subject-observations';
import { ObservationDetails } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation-details';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { NamedItemType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromLocal } from '@utils/convertor';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import {
    getNamedItemsByType,
    getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem,
    getNamedItemsByTypeVariables,
} from '@queries/generated/common.generated';
import SubjectActionsApi from '@apis/bff-api/subject-monitoring/subject-actions';
import { SubjectMonitoringStudyMemberApi } from '@apis/bff-api/subject-monitoring/subject-monitoring-study-member-api';
import SubjectObservationsApi from '@apis/bff-api/subject-monitoring/subject-observations';

export interface EditSubjectActionForm {
    type: SubjectObservationType;
    requestedDate: Date | null;
    dueDate: Date | null;
    owner: Owner | null;
    responsibleFunction: { ref: string; name: string; groupedBy: string } | null;
    description: string | null;
    comments: string | null;
    resolution: string | null;
    completedDate: Date | null;
    created_date: Date;
    updated_date: Date | null;
    created_by: string;
    updated_by: string | null;
}

interface Props {
    action: SubjectActionGridRow;
    studyRef: string | null;
    onSubmit: (id: number) => void;
    onClose: () => void;
}

const COMPONENT_NAME = 'edit-subject-action-dialog';

const EditSubjectActionDialog = ({ action, studyRef, onSubmit, onClose }: Props) => {
    const { t } = useTranslation('sm');

    const [commentsExpanded, setCommentsExpanded] = useState<boolean>(false);
    const [resolutionExpanded, setResolutionExpanded] = useState<boolean>(false);

    const { trigger: updateAction } = SubjectActionsApi.useUpdate(studyRef!);

    const { data: observation, isLoading: loadingObservation } = SubjectObservationsApi.useGetDetails(
        studyRef || '',
        action.observation_id,
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<EditSubjectActionForm>({
        mode: 'all',
        defaultValues: {
            type: SubjectObservationType.Manual,
            requestedDate: action.requested_date ? new Date(action.requested_date) : null,
            dueDate: action.due_date ? new Date(action.due_date) : null,
            owner: null,
            responsibleFunction: null,
            description: action.description,
            comments: action.comments ?? '',
            resolution: action.resolution ?? '',
            completedDate: action.completed_date ? new Date(action.completed_date) : null,
        },
    });

    const { data: studyMembersData, isLoading: isLoadingStudyMembers } =
        SubjectMonitoringStudyMemberApi.useGetAllStudyMembers(studyRef!, true);

    const studyMembers = useMemo(
        () =>
            studyMembersData
                ?.filter((user: StudyMemberDto) => !!user)
                .sort((a, b) => {
                    if (a?.acitve !== b?.acitve) {
                        return a?.acitve ? -1 : 1;
                    }

                    const aName = a?.name?.toLowerCase() ?? '';
                    const bName = b?.name?.toLowerCase() ?? '';
                    return aName.localeCompare(bName);
                })
                .map((user: StudyMemberDto) => ({
                    fullName: user.name as string,
                    ref: user.ref as string,
                    groupedBy: user.acitve ? 'Active' : 'Inactive',
                    disabled: action.owner
                        ? action.owner.toLowerCase() === user.name?.toLowerCase()
                            ? false
                            : !user.acitve
                        : !user.acitve,
                    active: user.acitve ?? false,
                })) ?? [],
        [studyMembersData, action.owner],
    );

    useEffect(() => {
        if (action.owner && studyMembers) {
            const currentOwner = studyMembers?.find((user) => user.fullName === action.owner) ?? null;
            // Use reset to set this as the default state (for correct isDirty logic)
            if (currentOwner) {
                reset((currentValues) => {
                    return { ...currentValues, owner: currentOwner };
                });
            }
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyMembers]);

    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } = useQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE, {
        variables: {
            type: NamedItemType.RESPONSIBLE_FUNCTION,
        },
    });

    const responsibleFunctions = useMemo(
        () =>
            responsibleFunctionsData?.namedItemsByType
                ?.filter(
                    (ni): ni is getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem => ni?.studyId === studyRef,
                )
                .sort((a, b) => {
                    if (a?.active !== b?.active) {
                        return a?.active ? -1 : 1;
                    }

                    if (a.name && b.name) {
                        return a.name?.localeCompare(b.name);
                    }

                    return a?.active ? -1 : 1;
                })
                .map((studyFunction) => ({
                    groupedBy: studyFunction.active ? 'Active' : 'Inactive',
                    name: studyFunction.name ?? '',
                    ref: (studyFunction.id as string) ?? '',
                    disabled: action.responsible_function
                        ? action.responsible_function.toLowerCase() === studyFunction?.name?.toLowerCase()
                            ? false
                            : !studyFunction.active
                        : !studyFunction.active,
                    active: studyFunction.active ?? false,
                })) ?? [],
        [responsibleFunctionsData?.namedItemsByType, action.responsible_function, studyRef],
    );

    useEffect(() => {
        if (action.responsible_function && responsibleFunctions.length > 0) {
            const currentFunction =
                responsibleFunctions?.find((studyFunction) => studyFunction.name === action.responsible_function) ??
                null;
            if (currentFunction) {
                reset((currentValues) => {
                    return { ...currentValues, responsibleFunction: currentFunction };
                });
            }
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [responsibleFunctions]);

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const dialogTitle = `${t('common:edit')} ${t('actionDialog.subjectTitle')}: ${action.record_id}`;

    const dialogHeight = 634;
    const dialogWidth = 520;

    const primaryBtnLabel = t('common:update');
    const createdBy = `${moment(action?.created_date).format('DD-MMM-YYYY')} by ${action?.created_by}`;
    const updatedBy = `${moment(action?.updated_date).format('DD-MMM-YYYY')} by ${action?.updated_by}`;

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    const onEditAction = async (data: EditSubjectActionForm) => {
        try {
            const response = await updateAction({
                actionId: Number(action.id),
                dueDate: data.dueDate ? stringDateWithoutTimeFromLocal(data.dueDate) : undefined,
                description: data.description,
                comments: data.comments,
                resolution: data.resolution,
                requestedDate: stringDateWithoutTimeFromLocal(data.requestedDate!)!,
                completedDate: data.completedDate ? stringDateWithoutTimeFromLocal(data.completedDate) : undefined,
                ownerRef: data.owner?.ref,
                studyFunctionRef: data.responsibleFunction?.ref,
            });

            if (response?.success) {
                return response.id!;
            }
            return 0;
        } catch {
            return 0;
        }
    };

    const submitAndClose = async (formData: EditSubjectActionForm) => {
        const id = await onEditAction(formData);
        onSubmit(id);
    };

    const handlePrimarySubmit = () => handleSubmit((data) => submitAndClose(data))();

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="edit-subject-action-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={handlePrimarySubmit}
            createdUpdatedInfo={<CreatedUpdatedInfo created={createdBy} updated={updatedBy} width={500} />}
            createdUpdatedBackgroundColour="gray"
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            initialHeight={dialogHeight}
            initialWidth={dialogWidth}
            additionalInfo={
                <ObservationDetails
                    observation={observation || undefined}
                    observationLoading={loadingObservation}
                    type={SubjectObservationType.Manual}
                    area="SM"
                />
            }
            additionalInfoWidth={385}
            additionalInfoBtnLabel={t('observation')}
            bodyPadding="none"
            setHeight="fit-content"
        >
            <div
                className={cn(
                    style.editSiteObservationDialog,
                    style.rightBorderStyle,
                    'k-w-full',
                    'k-h-full',
                    'k-d-flex-col',
                )}
            >
                {loadingObservation ? (
                    <Loading />
                ) : (
                    <div>
                        <div className={cn(style.editActionInfo)}>
                            <div className="k-shrink-0 k-d-flex k-justify-content-left">
                                <div className="k-d-flex k-flex-auto k-gap-sm">
                                    <div className="k-font-medium">{t('actionDialog.status')}</div>
                                    <div className={cn(style.editSiteObservationTopInfoLabel)}>{action.status}</div>
                                </div>
                            </div>
                        </div>
                        <form id="edit-subject-action" className="k-mt-sm k-w-full k-pl-3 k-pr-3">
                            <div
                                className={cn(
                                    'k-shrink-0 k-d-flex k-justify-content-center k-pb-md',
                                    style.formFieldGap,
                                )}
                            >
                                <div className="!k-basis-1/2">
                                    <DatePickerController
                                        control={control}
                                        name="requestedDate"
                                        data-testid="edit-subject-action-requested-date"
                                        fillMode="solid"
                                        required
                                        label={t('actionDialog.requestedDate')}
                                        readonly={isSubmitting}
                                    />
                                </div>
                                <div className="!k-basis-1/2">
                                    <DatePickerController
                                        control={control}
                                        name="dueDate"
                                        data-testid="edit-subject-action-due-date"
                                        fillMode="solid"
                                        label={t('actionDialog.dueDate')}
                                        readonly={isSubmitting}
                                    />
                                </div>
                            </div>
                            <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                                <BasicDropDownController
                                    label={t('actionDialog.owner')}
                                    control={control}
                                    name="owner"
                                    data-testid="edit-subject-action-owner"
                                    data={studyMembers ?? []}
                                    textField="fullName"
                                    groupField="groupedBy"
                                    dataItemKey="ref"
                                    filterable
                                    loading={isLoadingStudyMembers}
                                    allowClear
                                    activeField="active"
                                    readOnly={isSubmitting}
                                />
                            </div>
                            <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                                <BasicDropDownController
                                    label={t('actionDialog.responsibleFunction')}
                                    control={control}
                                    name="responsibleFunction"
                                    data-testid="edit-subject-action-responsible-function"
                                    data={responsibleFunctions ?? []}
                                    textField="name"
                                    groupField="groupedBy"
                                    dataItemKey="ref"
                                    filterable
                                    loading={loadingResponsibleFunctions}
                                    allowClear
                                    activeField="active"
                                    readOnly={isSubmitting}
                                />
                            </div>
                            <div className="k-shrink-0 k-d-flex k-justify-content-left k-gap-1 k-pb-md">
                                <div className="!k-basis-1/2">
                                    <DatePickerController
                                        control={control}
                                        name="completedDate"
                                        data-testid="edit-subject-action-completed-date"
                                        fillMode="solid"
                                        label={t('actionDialog.completedDate')}
                                        className="k-w-full"
                                        readonly={isSubmitting}
                                    />
                                </div>
                            </div>
                            <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                                <TextAreaController
                                    maxLength={3000}
                                    control={control}
                                    className={style.textAreaOverrides}
                                    name="description"
                                    required
                                    data-testid="edit-subject-action-description"
                                    label={t('actionDialog.description')}
                                    rows={4}
                                    readOnly={isSubmitting}
                                />
                            </div>
                            <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                                <TextAreaDropdownController
                                    maxLength={3000}
                                    control={control}
                                    className={style.textAreaOverrides}
                                    name="comments"
                                    data-testid="edit-subject-action-comments"
                                    label={t('actionDialog.comments')}
                                    rows={4}
                                    handleInputChange={async (event) => {
                                        event.value.length > 0 ? setCommentsExpanded(true) : setCommentsExpanded(false);
                                    }}
                                    expanded={commentsExpanded || !!action.comments}
                                    readOnly={isSubmitting}
                                />
                            </div>
                            <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                                <TextAreaDropdownController
                                    maxLength={3000}
                                    control={control}
                                    className={style.textAreaOverrides}
                                    name="resolution"
                                    data-testid="edit-subject-action-resolution"
                                    label={t('actionDialog.resolution')}
                                    rows={4}
                                    handleInputChange={async (event) => {
                                        event.value.length > 0
                                            ? setResolutionExpanded(true)
                                            : setResolutionExpanded(false);
                                    }}
                                    expanded={resolutionExpanded || !!action.resolution}
                                    readOnly={isSubmitting}
                                />
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </DraggableDialog>
    );
};

export default EditSubjectActionDialog;
